import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useOrder } from "../context/OrderContext";
import { BUSINESS, IMG, NAV_LINKS } from "../data/content";
import { scrollToHash } from "../lib/scroll";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const openOrder = useOrder();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || open || pathname !== "/";

  const handleNav = (e, href) => {
    if (!href.startsWith("#")) return;
    e.preventDefault();
    setOpen(false);
    if (pathname === "/") {
      if (href === "#home") window.scrollTo({ top: 0, behavior: "smooth" });
      else scrollToHash(href);
    } else {
      navigate("/" + (href === "#home" ? "" : href));
    }
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,box-shadow,border-color] duration-500 ${
        solid
          ? "bg-background/85 backdrop-blur-xl border-b border-surface/60 shadow-warm"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-2 lg:grid-cols-[1fr_auto_1fr] items-center h-20">
          {/* Left links (desktop) */}
          <ul className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.slice(0, 3).map((l) => (
              <li key={l.href}>
                <a
                  href={l.href}
                  onClick={(e) => handleNav(e, l.href)}
                  data-testid={`nav-${l.label.toLowerCase()}-link`}
                  className={`text-sm tracking-wide transition-colors duration-300 hover:text-highlight ${
                    solid ? "text-foreground" : "text-background drop-shadow"
                  }`}
                >
                  {l.label}
                </a>
              </li>
            ))}
          </ul>

          {/* Center logo */}
          <a
            href="#home"
            onClick={(e) => handleNav(e, "#home")}
            data-testid="nav-logo"
            className="justify-self-start lg:justify-self-center flex items-center"
          >
            <img
              src={IMG.logo}
              alt="Detour Coffee Co."
              className={`h-9 sm:h-10 w-auto transition-[filter] duration-500 ${
                solid ? "" : "brightness-0 invert drop-shadow-lg"
              }`}
            />
          </a>

          {/* Right links + CTA (desktop) */}
          <div className="hidden lg:flex items-center justify-end gap-8">
            {NAV_LINKS.slice(3).map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => handleNav(e, l.href)}
                data-testid={`nav-${l.label.toLowerCase()}-link`}
                className={`text-sm tracking-wide transition-colors duration-300 hover:text-highlight ${
                  solid ? "text-foreground" : "text-background drop-shadow"
                }`}
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/gift-cards"
              data-testid="nav-gift-cards-link"
              className={`text-sm tracking-wide transition-colors duration-300 hover:text-highlight ${
                solid ? "text-foreground" : "text-background drop-shadow"
              }`}
            >
              Gift Cards
            </Link>
            <button
              onClick={openOrder}
              data-testid="nav-order-online-btn"
              className="rounded-full bg-primary text-primary-foreground px-6 py-2.5 text-sm font-semibold tracking-wide transition-transform duration-300 hover:scale-[1.03] active:scale-[0.98]"
            >
              Order Online
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Toggle menu"
            className={`lg:hidden justify-self-end p-2 rounded-full transition-colors ${
              solid ? "text-foreground" : "text-background"
            }`}
          >
            {open ? <X size={26} strokeWidth={1.5} /> : <Menu size={26} strokeWidth={1.5} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeInOut" }}
            className="lg:hidden overflow-hidden bg-background/95 backdrop-blur-xl border-t border-surface/60"
            data-testid="mobile-menu"
          >
            <ul className="px-6 py-6 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <li key={l.href}>
                  <a
                    href={l.href}
                    onClick={(e) => handleNav(e, l.href)}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}-link`}
                    className="block py-3 font-serif text-2xl text-foreground hover:text-highlight transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
              <li>
                <Link
                  to="/gift-cards"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-gift-cards-link"
                  className="block py-3 font-serif text-2xl text-foreground hover:text-highlight transition-colors"
                >
                  Gift Cards
                </Link>
              </li>
              <button
                onClick={() => { setOpen(false); openOrder(); }}
                data-testid="mobile-order-online-btn"
                className="mt-4 w-full text-center rounded-full bg-primary text-primary-foreground px-6 py-3.5 text-sm font-semibold tracking-wide"
              >
                Order Online
              </button>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
